import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Building2, CheckCircle2, Briefcase, Send, Inbox, Bell } from "lucide-react";
import api from "../api";
import VacancyCard from "../components/VacancyCard";
import { useAuth } from "../AuthContext";

export default function Home() {
  const { user } = useAuth();
  const [vacancies, setVacancies] = useState([]);
  const [personalStats, setPersonalStats] = useState(null);

  useEffect(() => {
    api.get("/vacancies/recent?limit=6").then(({ data }) => setVacancies(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || user === false) { setPersonalStats(null); return; }
    if (user.role === "candidate") {
      Promise.all([
        api.get("/applications/mine").catch(() => ({ data: [] })),
        api.get("/saved-vacancies").catch(() => ({ data: [] })),
        api.get("/profile/candidate").catch(() => ({ data: null })),
      ]).then(([apps, saved, profile]) => {
        setPersonalStats({
          role: "candidate",
          applications: apps.data.length,
          inReview: apps.data.filter((a) => a.status === "ingediend").length,
          proposed: apps.data.filter((a) => a.status === "aangeboden").length,
          saved: saved.data.length,
          profile: profile.data,
        });
      });
    } else if (user.role === "client") {
      Promise.all([
        api.get("/proposals/mine").catch(() => ({ data: [] })),
        api.get("/profile/client").catch(() => ({ data: null })),
      ]).then(([props, profile]) => {
        setPersonalStats({
          role: "client",
          proposals: props.data.length,
          interesting: props.data.filter((p) => p.status === "interessant").length,
          pending: props.data.filter((p) => p.status === "verstuurd").length,
          profile: profile.data,
        });
      });
    }
  }, [user]);

  const isLoggedInUser = user && user !== false && (user.role === "candidate" || user.role === "client");

  return (
    <>
      {isLoggedInUser && <PersonalHero user={user} stats={personalStats} />}
      {/* HERO (public — for anonymous + admin) */}
      {!isLoggedInUser && (
      <section className="relative overflow-hidden bg-[#f7f6f2]">
        <div className="absolute inset-0 ok-hero-grid opacity-60" />
        <div className="ok-container relative grid grid-cols-1 lg:grid-cols-12 gap-12 py-20 lg:py-28">
          <div className="lg:col-span-7">
            <div className="ok-eyebrow mb-5">Werk maken van kennis</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
              Wij leveren <span className="text-[#01696f]">kracht</span><br />
              aan de publieke sector.
            </h1>
            <p className="mt-6 text-lg text-[#4a5d5e] max-w-xl leading-relaxed">
              Overheidskrachten verbindt ervaren professionals met gemeenten, provincies, rijksoverheid en waterschappen, via Flextender en directe inhuur.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/aanmelden-kandidaat"
                data-testid="hero-cta-candidate"
                className="inline-flex items-center gap-2 bg-[#01696f] text-white px-6 py-3 rounded-sm hover:bg-[#015257] transition-colors font-medium"
              >
                Aanmelden als kandidaat <ArrowRight size={16} />
              </Link>
              <Link
                to="/aanmelden-inlener"
                data-testid="hero-cta-vacancies"
                className="inline-flex items-center gap-2 border border-[#0a191a] px-6 py-3 rounded-sm hover:bg-[#0a191a] hover:text-white transition-colors font-medium"
              >
                Aanmelden als inlener
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-xs text-[#4a5d5e]">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#01696f]" /> Interim opdrachten bij de overheid</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#01696f]" /> Detacheren & ZZP</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#01696f]" /> Jij staat centraal</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-[#e2e8f0]">
              <img
                src="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg"
                alt="Professioneel team in overleg"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-[#e2e8f0] p-5 max-w-[240px] hidden md:block">
              <div className="text-3xl font-display font-bold text-[#01696f]">120+</div>
              <div className="text-xs text-[#4a5d5e] mt-1 leading-tight">Geplaatste professionals bij overheidsorganisaties</div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* RECENT VACANCIES */}
      <section className="ok-section">
        <div className="ok-container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="ok-eyebrow mb-3">Open vacatures</div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                Recente opdrachten in de publieke sector
              </h2>
            </div>
            <Link to="/vacatures" data-testid="all-vacancies-link" className="ok-link-arrow hidden md:inline-flex">
              Alle vacatures →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {vacancies.map((v) => <VacancyCard key={v.id} v={v} />)}
          </div>
          {vacancies.length === 0 && (
            <p className="text-sm text-[#7b8e8f]">Er zijn momenteel geen open vacatures.</p>
          )}

          <div className="mt-12 bg-[#01696f] text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="ok-eyebrow" style={{ color: "#ffffff" }}>WhatsApp kanaal</div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold mt-3 max-w-2xl leading-tight" style={{ color: "#ffffff" }}>
                Sneller op de hoogte worden gebracht?
              </h3>
              <p className="text-white/85 mt-3 max-w-xl text-sm leading-relaxed">
                Abonneer je op ons WhatsAppkanaal en ontvang nieuwe overheidsopdrachten direct op je telefoon, vaak voordat ze openbaar gepubliceerd worden.
              </p>
            </div>
            <a
              href="https://whatsapp.com/channel/0029Vb84JuW72WU4oIK6CS02"
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-cta"
              className="inline-flex items-center gap-2 bg-white text-[#01696f] px-6 py-3 font-medium rounded-sm hover:bg-[#f7f6f2] transition-colors whitespace-nowrap"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.71 5.516l-.999 3.648 3.778-.863zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
              </svg>
              Abonneer op WhatsApp kanaal
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#f7f6f2] ok-section">
        <div className="ok-container">
          <div className="ok-eyebrow mb-3">Het proces</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-12 max-w-2xl">
            Drie stappen tot de juiste plaatsing.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e2e8f0] border border-[#e2e8f0]">
            {[
              { n: "01", t: "Aanmelden", d: "Kandidaten registreren met cv en expertisegebied. Inleners delen hun behoefte en organisatie." },
              { n: "02", t: "Matchen", d: "Wij maken op basis van skills, beschikbaarheid en uurtarief een gerichte selectie." },
              { n: "03", t: "Plaatsen", d: "Voorstel via Flextender of directe inhuur, contract en zorgvuldige nazorg." },
            ].map((s) => (
              <div key={s.n} className="bg-white p-8">
                <div className="font-display text-5xl font-bold text-[#01696f] leading-none">{s.n}</div>
                <h3 className="font-display text-xl font-semibold mt-6">{s.t}</h3>
                <p className="text-sm text-[#4a5d5e] mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* DUAL CTA */}
      <section className="ok-section">
        <div className="ok-container grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-[#e2e8f0] p-8">
            <Users className="text-[#01696f]" />
            <h3 className="font-display text-2xl font-semibold mt-4">Ben je professional?</h3>
            <p className="text-sm text-[#4a5d5e] mt-3 max-w-md">
              Werk via Overheidskrachten op uitdagende opdrachten in de publieke sector. Detacheren of ZZP.
            </p>
            <Link to="/aanmelden-kandidaat" data-testid="footer-cta-candidate" className="ok-link-arrow mt-6 inline-flex">
              Aanmelden als kandidaat →
            </Link>
          </div>
          <div className="border border-[#e2e8f0] p-8 bg-[#0a191a] text-white">
            <Building2 className="text-[#a8d8db]" />
            <h3 className="font-display text-2xl font-semibold mt-4">Ben je inlener?</h3>
            <p className="text-sm text-white/70 mt-3 max-w-md">
              Wij leveren snel passende professionals voor uw opdracht. Direct of via Flextender.
            </p>
            <Link to="/aanmelden-inlener" data-testid="footer-cta-client" className="mt-6 inline-flex items-center gap-2 text-[#a8d8db] font-semibold">
              Inlener aanmelden →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function PersonalHero({ user, stats }) {
  const name = user.name || user.email;
  const firstName = name.split(" ")[0];

  if (user.role === "candidate") {
    return (
      <section className="relative overflow-hidden bg-gradient-to-br from-[#01696f] to-[#015257] text-white">
        <div className="absolute inset-0 opacity-10 ok-hero-grid" />
        <div className="ok-container relative py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="ok-eyebrow" style={{ color: "rgba(255,255,255,0.85)" }}>Welkom terug</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-3" style={{ color: "#ffffff" }} data-testid="personal-hero-title">
                Welkom, {firstName}.
              </h1>
              <p className="mt-5 text-lg max-w-xl leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                Bekijk de laatste matchende opdrachten of werk je profiel bij, zodat we sneller een passende plaatsing voor je vinden.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/vacatures" data-testid="hero-personal-vacancies" className="inline-flex items-center gap-2 bg-white text-[#01696f] px-6 py-3 rounded-sm hover:bg-[#f7f6f2] font-medium transition-colors">
                  Matchende vacatures <ArrowRight size={16} />
                </Link>
                <Link to="/mijn-sollicitaties" data-testid="hero-personal-applications" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-sm hover:bg-white/10 font-medium transition-colors">
                  Mijn sollicitaties
                </Link>
                <Link to="/mijn-opgeslagen" data-testid="hero-personal-saved" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-sm hover:bg-white/10 font-medium transition-colors">
                  Mijn opgeslagen vacatures
                </Link>
                <Link to="/kandidaat-dashboard" data-testid="hero-personal-profile" className="inline-flex items-center gap-2 text-white/80 hover:text-white px-3 py-3 font-medium underline underline-offset-4">
                  Profiel bewerken
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              <PersonalStat icon={Send} label="Sollicitaties" value={stats?.applications ?? "..."} testid="stat-applications" />
              <PersonalStat icon={Inbox} label="In behandeling" value={stats?.inReview ?? "..."} testid="stat-in-review" />
              <PersonalStat icon={Briefcase} label="Aangeboden" value={stats?.proposed ?? "..."} testid="stat-proposed" />
              <PersonalStat icon={Bell} label="Opgeslagen" value={stats?.saved ?? "..."} testid="stat-saved" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // client
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#01696f] to-[#015257] text-white">
      <div className="absolute inset-0 opacity-10 ok-hero-grid" />
      <div className="ok-container relative py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="ok-eyebrow" style={{ color: "rgba(255,255,255,0.85)" }}>Welkom terug</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-3" style={{ color: "#ffffff" }} data-testid="personal-hero-title">
              Welkom, {stats?.profile?.organization_name || firstName}.
            </h1>
            <p className="mt-5 text-lg max-w-xl leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
              Bekijk voorgestelde kandidaten of plaats een nieuwe inhuuropdracht. Onze recruiters staan klaar om u te ondersteunen.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/inlener-dashboard" data-testid="hero-personal-proposals" className="inline-flex items-center gap-2 bg-white text-[#01696f] px-6 py-3 rounded-sm hover:bg-[#f7f6f2] font-medium transition-colors">
                Bekijk voorstellen <ArrowRight size={16} />
              </Link>
              <Link to="/vacatures" data-testid="hero-personal-vacancies-client" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-sm hover:bg-white/10 font-medium transition-colors">
                Open opdrachten
              </Link>
              <a href="mailto:info@overheidskrachten.nl" className="inline-flex items-center gap-2 text-white/80 hover:text-white px-3 py-3 font-medium underline underline-offset-4">
                Recruiter contacten
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <PersonalStat icon={Inbox} label="Voorstellen totaal" value={stats?.proposals ?? "..."} testid="stat-proposals" />
            <PersonalStat icon={Bell} label="Open" value={stats?.pending ?? "..."} testid="stat-pending" />
            <PersonalStat icon={CheckCircle2} label="Interessant" value={stats?.interesting ?? "..."} testid="stat-interesting" />
            <PersonalStat icon={Building2} label="Organisatie" value={stats?.profile?.organization_type || "..."} testid="stat-org" small />
          </div>
        </div>
      </div>
    </section>
  );
}

function PersonalStat({ icon: Icon, label, value, testid, small }) {
  return (
    <div className="bg-white/10 border border-white/20 backdrop-blur-sm p-5" data-testid={testid}>
      <Icon size={18} className="text-white/80" />
      <div className="font-display font-bold mt-3" style={{ color: "#ffffff", fontSize: small ? "1rem" : "2rem", lineHeight: 1 }}>
        {value}
      </div>
      <div className="text-xs uppercase tracking-wider mt-2" style={{ color: "rgba(255,255,255,0.75)" }}>{label}</div>
    </div>
  );
}

const FAQ_ITEMS = [
  {
    q: "Wat doet Overheidskrachten precies?",
    a: "Overheidskrachten verbindt overheidsorganisaties met gekwalificeerde professionals op het gebied van juridische, financiële en data-gerelateerde vraagstukken. Of het nu gaat om een acute behoefte of een langdurige uitdaging, wij zorgen voor de juiste expertise op het juiste moment.",
  },
  {
    q: "Wat zijn de voordelen van Overheidskrachten voor werkgevers?",
    a: "Via Overheidskrachten krijgt u snel toegang tot specialistische kennis, zonder de doorlooptijd van een traditioneel wervingstraject. U bepaalt zelf de duur en omvang van de inzet, terwijl wij alle contractuele en administratieve zaken afhandelen. Omdat onze professionals bij ons in vaste dienst zijn, hoeft u zich geen zorgen te maken over risico's rondom de wet DBA.",
  },
  {
    q: "Hoe lang kan een tijdelijke opdracht duren?",
    a: "Dat verschilt per situatie. Een opdracht loopt gemiddeld tussen de zes en twaalf maanden, maar er is geen vaste eindtermijn. Wanneer een professional klaar is voor een nieuwe uitdaging, denken wij proactief mee over een passende opvolging of taakverzwaring. Een goede overdracht is daarbij vanzelfsprekend, we begeleiden de overgang zodat continuïteit gewaarborgd blijft.",
  },
  {
    q: "Hoe verloopt het proces als ik een professional wil inhuren?",
    a: "Nadat u contact heeft opgenomen, wordt u gekoppeld aan een relatiemanager die thuis is in uw vakgebied. Samen brengen we de opdracht in kaart. Voor enkelvoudige plaatsingen kunnen we doorgaans binnen één à twee werkdagen een passend profiel presenteren. Bij complexere of bredere opdrachten schakelen we extra expertise in om tot het meest geschikte voorstel te komen.",
  },
  {
    q: "Zijn de professionals vast in dienst bij Overheidskrachten?",
    a: "Ja, het overgrote deel van onze professionals heeft een vast contract bij Overheidskrachten. Dat betekent dat u als opdrachtgever niet het risico loopt op naheffingen of andere consequenties in het kader van de wet DBA, een risico dat bij inzet van zelfstandigen of via bemiddelingsbureaus sinds de verscherpte handhaving per januari 2025 wel degelijk speelt.",
  },
];

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="ok-section" data-testid="faq-section">
      <div className="ok-container max-w-4xl">
        <div className="ok-eyebrow mb-3">Veelgestelde vragen</div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-10">
          Antwoorden op de meest gestelde vragen.
        </h2>
        <div className="border-t border-[#e2e8f0]">
          {FAQ_ITEMS.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} className="border-b border-[#e2e8f0]">
                <button
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={open}
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 md:py-6 text-left hover:text-[#01696f] transition-colors"
                >
                  <span className="font-display text-lg md:text-xl font-semibold pr-4">{item.q}</span>
                  <span
                    aria-hidden="true"
                    className={`shrink-0 grid place-items-center w-8 h-8 rounded-full border transition-all ${open ? "bg-[#01696f] text-white border-[#01696f] rotate-45" : "border-[#e2e8f0] text-[#01696f]"}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
                <div
                  data-testid={`faq-answer-${i}`}
                  className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="text-[#4a5d5e] leading-relaxed pr-12 max-w-3xl">
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
